/* eslint-disable no-console */
import {
  types,
  flow,
  applySnapshot,
  getRoot,
  getSnapshot
} from "mobx-state-tree";

import { ApiStore } from "./ApiStore";
import GenericJSONPrimitive from "./GenericJSONPrimitive";
import { Measurement } from "./MeasurementStore";
import { SensorGroup } from "./SensorGroupStore";

const Sensor = types.compose(
  ApiStore,
  types
    .model({
      id: types.identifier,
      measurements: types.array(
        types.late(() => types.frozen(Measurement)) // Frozen for performance
      ),
      sensor_groups: types.array(
        types.late(() => types.reference(SensorGroup))
      ),
      external_id: types.optional(types.string, ""),
      human_name: types.optional(types.string, ""),
      recent_measurement: types.maybeNull(types.late(() => Measurement)),
      location: types.optional(GenericJSONPrimitive),
      calibration: types.optional(GenericJSONPrimitive),
      notes: types.optional(types.string, ""),
      data_ready_timestamp: types.maybeNull(types.string, ""),
      data_unready_timestamp: types.maybeNull(types.string, "")
    })
    .actions(self => ({
      pullMeasurements: flow(function* pullMeasurements() {
        const notifications = getRoot(self).notifications();

        try {
          const res = yield self.fetch2(`/sensors/${self.id}/measurements/`);
          const json = yield res.json();

          self.measurements = json;

          yield Promise.resolve();
        } catch (err) {
          console.error(
            "Failed to fetch and update sensor measurements.\n",
            err
          );
          notifications.error("Unknown error occured.");
        }
      }),
      updateMeasurements: flow(function* updateMeasurements(measurementsJson) {
        self.measurements = measurementsJson;
        yield Promise.resolve();
      }),
      fetchAndUpdateMeasurements: flow(function* fetchAndUpdateMeasurements() {
        yield self.fetch(
          `/sensors/${self.id}/measurements/`,
          {},
          self.updateMeasurements,
          err =>
            console.error(
              "Failed to fetch and update sensor measurements.\n",
              err
            )
        );
      }),
      partialUpdate: flow(function* partialUpdate(updatedJson) {
        const notifications = getRoot(self).notifications();

        try {
          const updated = {
            ...updatedJson
          };

          const res = yield self.fetch2(`/sensors/${self.id}/`, {
            method: "PATCH",
            body: JSON.stringify(updated)
          });
          const json = yield res.json();

          const combinedSnapshot = {
            ...getSnapshot(self),
            ...json
          };

          applySnapshot(self, combinedSnapshot);
        } catch (err) {
          console.error("Failed to update sensor.\n", err);
          notifications.error("Unknown error occured.");
        }
      })
    }))
    .views(self => ({
      orderedMeasurements() {
        return getSnapshot(self.measurements);
      },

      notesFirstValue() {
        return (self.notes || "").split("\n")[0].split(" ")[0];
      }
    }))
);

const SensorStore = types.compose(
  ApiStore,
  types
    .model({
      sensors: types.map(Sensor)
    })
    .actions(self => ({
      update: flow(function* update(sensorsJson) {
        sensorsJson.forEach(sensorJson => {
          self.sensors.put(sensorJson);
        });
        yield Promise.resolve();
      }),
      fetchAndUpdate: flow(function* fetchAndUpdate() {
        yield self.fetch("/sensors/", {}, self.update, err =>
          console.error("Failed to fetch and update sensors.\n", err)
        );
      }),
      reset: flow(function* reset() {
        applySnapshot(self, {});
        yield Promise.resolve();
      })
    }))
);

export { Sensor, SensorStore };
